import { partitionStationSections } from '@app/core/stations/partition-station-sections';
import { useStationsGroups } from '@app/core/stations/hooks/useStationsGroupsQuery';
import { MinimumStationsGroup, Station } from '@app/core/types/stations';
import { useMemo } from 'react';

interface UseSortedFilteredStationsParams {
  readonly stations: readonly Station[];
  readonly recentStationIds: readonly string[];
  readonly favoriteStationIds: readonly string[];
  readonly query: string;
}

export interface SharedGroupSelectorSection {
  group: MinimumStationsGroup;
  stations: Station[];
}

interface SelectorStationSections {
  sortedStations: Station[];
  recentStations: Station[];
  favoriteStations: Station[];
  ownStations: Station[];
  individuallySharedStations: Station[];
  sharedGroupSections: SharedGroupSelectorSection[];
}

interface UseSortedFilteredStationsResult extends SelectorStationSections {
  readonly filteredStations: readonly Station[];
}

/**
 * Partitions stations by recents, favorites, own, individually shared, and shared groups.
 * Search query is applied to the sorted list; sections keep the same partition rules.
 */
export function useSortedFilteredStations({
  stations,
  recentStationIds,
  favoriteStationIds,
  query,
}: UseSortedFilteredStationsParams): UseSortedFilteredStationsResult {
  const stationsGroups = useStationsGroups();

  const sections = useMemo(
    () => partitionSelectorStations(stations, recentStationIds, favoriteStationIds, stationsGroups),
    [stations, recentStationIds, favoriteStationIds, stationsGroups],
  );

  const filteredStations = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      return sections.sortedStations;
    }
    return sections.sortedStations.filter(
      (s) => s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    );
  }, [query, sections.sortedStations]);

  const filteredSections = useMemo(
    () =>
      partitionSelectorStations(
        filteredStations,
        recentStationIds,
        favoriteStationIds,
        stationsGroups,
      ),
    [filteredStations, recentStationIds, favoriteStationIds, stationsGroups],
  );

  return {
    sortedStations: sections.sortedStations,
    filteredStations,
    recentStations: filteredSections.recentStations,
    favoriteStations: filteredSections.favoriteStations,
    ownStations: filteredSections.ownStations,
    individuallySharedStations: filteredSections.individuallySharedStations,
    sharedGroupSections: filteredSections.sharedGroupSections,
  };
}

/**
 * Splits stations into navigator sections. Each station appears in exactly one section:
 * recent, favorite, own, individually shared, or a shared group.
 */
function partitionSelectorStations(
  stations: readonly Station[],
  recentStationIds: readonly string[],
  favoriteStationIds: readonly string[],
  stationsGroups: MinimumStationsGroup[],
): SelectorStationSections {
  const recentIdsSet = new Set(recentStationIds);
  const favoriteIdsSet = new Set(favoriteStationIds);
  const recentPool: Station[] = [];
  const favoritePool: Station[] = [];
  const nonFavorites: Station[] = [];

  stations.forEach((station) => {
    if (recentIdsSet.has(station.id)) {
      recentPool.push(station);
    } else if (favoriteIdsSet.has(station.id)) {
      favoritePool.push(station);
    } else {
      nonFavorites.push(station);
    }
  });

  const recentStations = recentStationIds
    .map((id) => recentPool.find((station) => station.id === id))
    .filter((station): station is Station => station !== undefined);

  const favoriteStations = favoriteStationIds
    .map((id) => favoritePool.find((station) => station.id === id))
    .filter((station): station is Station => station !== undefined);

  const { ownStations, individuallySharedStations, sharedGroupSections } = partitionStationSections(
    nonFavorites,
    stationsGroups,
  );

  const sortedStations = [
    ...recentStations,
    ...favoriteStations,
    ...ownStations,
    ...individuallySharedStations,
    ...sharedGroupSections.flatMap((section) => section.stations),
  ];

  return {
    sortedStations,
    recentStations,
    favoriteStations,
    ownStations,
    individuallySharedStations,
    sharedGroupSections,
  };
}
